import React from 'react';
import { stringToColor } from '@/../../shared/src/utils/Colors';
import { AVAILABLE_COLORS } from '@/constants';

interface CommenterInfo {
    initial: string;
    id: string; // Used for key, maybe color later
}

interface CommenterInitialsIconProps {
    commenters: CommenterInfo[];
    className?: string;
}

// Tailwind class for specific rounded corners (top-left, top-right, bottom-right)
const roundedCornersClass = 'rounded-tr-md rounded-br-md rounded-tl-md';

// Add common non-interactive/selectable classes
const nonSelectableClasses = 'select-none pointer-events-none user-select-none';

export const CommenterInitialsIcon: React.FC<CommenterInitialsIconProps> = ({ commenters, className }) => {
    if (!commenters || commenters.length === 0) {
        return null; // Or return a default icon/placeholder
    }

    // Take up to the first 3 unique commenters passed in props
    const firstCommenter = commenters[0];
    const secondCommenter = commenters.length > 1 ? commenters[1] : null;
    const thirdCommenter = commenters.length > 2 ? commenters[2] : null;

    // Calculate colors based on user ID using the imported constants/functions
    const firstColor = stringToColor(firstCommenter.id, AVAILABLE_COLORS);
    const secondColor = secondCommenter ? stringToColor(secondCommenter.id, AVAILABLE_COLORS) : null;
    const thirdColor = thirdCommenter ? stringToColor(thirdCommenter.id, AVAILABLE_COLORS) : null;

    return (
        <div className={`relative flex items-center justify-center w-5 h-5 ${className}`}>
            {/* Third commenter - offset further right, highest z-index */}
            {thirdCommenter && thirdColor && (
                <div
                    key={thirdCommenter.id}
                    className={`absolute top-0 left-0 w-5 h-5 ${roundedCornersClass} ${nonSelectableClasses} flex items-center justify-center text-white text-xs font-semibold z-20`}
                    style={{
                        backgroundColor: thirdColor,
                        transform: 'translateX(12px)',
                    }}
                    title={`Commenter: ${thirdCommenter.initial}`}
                    tabIndex={-1}
                    aria-hidden="true"
                    contentEditable={false}
                >
                    {thirdCommenter.initial.toUpperCase()}
                </div>
            )}
            {/* Second commenter - offset right, middle z-index */}
            {secondCommenter && secondColor && (
                <div
                    key={secondCommenter.id}
                    className={`absolute top-0 left-0 w-5 h-5 ${roundedCornersClass} ${nonSelectableClasses} flex items-center justify-center text-white text-xs font-semibold z-10`}
                    style={{
                        backgroundColor: secondColor,
                        transform: 'translateX(6px)',
                    }}
                    title={`Commenter: ${secondCommenter.initial}`}
                    tabIndex={-1}
                    aria-hidden="true"
                    contentEditable={false}
                >
                    {secondCommenter.initial.toUpperCase()}
                </div>
            )}
            {/* First commenter - at top-left, lowest z-index */}
            <div
                key={firstCommenter.id}
                className={`absolute top-0 left-0 w-5 h-5 ${roundedCornersClass} ${nonSelectableClasses} flex items-center justify-center text-white text-xs font-semibold z-0 shadow-sm`}
                style={{ backgroundColor: firstColor }}
                title={`Commenter: ${firstCommenter.initial}`}
                tabIndex={-1}
                aria-hidden="true"
                contentEditable={false}
            >
                {firstCommenter.initial.toUpperCase()}
            </div>
        </div>
    );
};
